import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useAuth } from "../../context/AuthContext";

const SupportContext = createContext();

function useProvideSupport() {
    const { user } = useAuth();

    // State
    const [supportAccess, setSupportAccess] = useState(null);
    const [supportPlans, setSupportPlans] = useState([]);
    const [supportSubscription, setSupportSubscription] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [currentTicket, setCurrentTicket] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
    });

    // API hooks
    const {
        call: fetchSupportAccess,
        isLoading: isLoadingAccess,
        error: accessError,
    } = useBluelagoon({ method: "getSupportAccess" });

    const {
        call: fetchSupportPlans,
        isLoading: isLoadingPlans,
        error: plansError,
    } = useBluelagoon({ method: "getSupportPlans" });

    const {
        call: fetchSupportSubscription,
        isLoading: isLoadingSubscription,
        error: subscriptionError,
    } = useBluelagoon({ method: "getSupportSubscription" });

    const {
        call: fetchTickets,
        isLoading: isLoadingTickets,
        error: ticketsError,
    } = useBluelagoon({ method: "getTickets" });

    const {
        call: fetchTicket,
        isLoading: isLoadingTicket,
        error: ticketError,
    } = useBluelagoon({ method: "getTicket" });

    const {
        call: createTicketApi,
        isLoading: isCreatingTicket,
        error: createTicketError,
    } = useBluelagoon({ method: "createTicket" });

    const {
        call: addReplyApi,
        isLoading: isAddingReply,
        error: addReplyError,
    } = useBluelagoon({ method: "addTicketReply" });

    const {
        call: createCheckoutApi,
        isLoading: isCreatingCheckout,
        error: checkoutError,
    } = useBluelagoon({ method: "createSupportCheckout" });

    const {
        call: cancelSupportSubscriptionApi,
        isLoading: isCancelingSubscription,
        error: cancelError,
    } = useBluelagoon({ method: "cancelSupportSubscription" });

    const {
        call: pauseSupportSubscriptionApi,
        isLoading: isPausingSubscription,
        error: pauseError,
    } = useBluelagoon({ method: "pauseSupportSubscription" });

    const {
        call: resumeSupportSubscriptionApi,
        isLoading: isResumingSubscription,
        error: resumeError,
    } = useBluelagoon({ method: "resumeSupportSubscription" });

    // Fetch support access on mount
    const getSupportAccess = useCallback(async () => {
        try {
            const response = await fetchSupportAccess({});
            if (response?.data) {
                setSupportAccess(response.data);
            }
            return response;
        } catch (error) {
            console.error("Error fetching support access:", error);
            return null;
        }
    }, [fetchSupportAccess]);

    // Fetch support plans
    const getSupportPlans = useCallback(async () => {
        try {
            const response = await fetchSupportPlans({});
            if (response?.data?.plans) {
                setSupportPlans(response.data.plans);
            }
            return response;
        } catch (error) {
            console.error("Error fetching support plans:", error);
            return null;
        }
    }, [fetchSupportPlans]);

    // Fetch support subscription
    const getSupportSubscription = useCallback(async () => {
        try {
            const response = await fetchSupportSubscription({});
            if (response?.data) {
                setSupportSubscription(response.data);
            }
            return response;
        } catch (error) {
            console.error("Error fetching support subscription:", error);
            return null;
        }
    }, [fetchSupportSubscription]);

    // Fetch tickets with pagination
    const getTickets = useCallback(async (page = 1, limit = 10, status = null) => {
        try {
            const params = { page, limit };
            if (status) params.status = status;

            const response = await fetchTickets(params);
            if (response?.data) {
                setTickets(response.data.tickets || []);
                setPagination({
                    page: response.data.page || page,
                    limit: response.data.limit || limit,
                    total: response.data.total || 0,
                    totalPages: response.data.totalPages || 0,
                });
            }
            return response;
        } catch (error) {
            console.error("Error fetching tickets:", error);
            return null;
        }
    }, [fetchTickets]);

    // Fetch single ticket with messages
    const getTicket = useCallback(async (ticketId) => {
        try {
            const response = await fetchTicket({ id: ticketId });
            if (response?.data) {
                setCurrentTicket(response.data);
            }
            return response;
        } catch (error) {
            console.error("Error fetching ticket:", error);
            return null;
        }
    }, [fetchTicket]);

    // Create new ticket
    const createTicket = useCallback(async (subject, message, priority = "NORMAL", attachments = []) => {
        try {
            const response = await createTicketApi({ subject, message, priority, attachments });
            if (response?.data) {
                // Refresh tickets list
                await getTickets(1, pagination.limit);
            }
            return response;
        } catch (error) {
            console.error("Error creating ticket:", error);
            throw error;
        }
    }, [createTicketApi, getTickets, pagination.limit]);

    // Add reply to ticket
    const addReply = useCallback(async (ticketId, message, attachments = []) => {
        try {
            const response = await addReplyApi({ id: ticketId, message, attachments });
            if (response?.data) {
                // Refresh current ticket
                await getTicket(ticketId);
            }
            return response;
        } catch (error) {
            console.error("Error adding reply:", error);
            throw error;
        }
    }, [addReplyApi, getTicket]);

    // Create checkout session for support subscription
    const createSupportCheckout = useCallback(async (tier, isYearly = false, isLatam = false) => {
        try {
            const response = await createCheckoutApi({
                tier,
                isYearly,
                isLatam,
                successUrl: `${window.location.origin}/billing#support`,
                cancelUrl: `${window.location.origin}/billing#support`,
            });
            if (response?.data?.url) {
                window.location.href = response.data.url;
            }
            return response;
        } catch (error) {
            console.error("Error creating checkout:", error);
            throw error;
        }
    }, [createCheckoutApi]);

    // Clear current ticket when navigating away
    const clearCurrentTicket = useCallback(() => {
        setCurrentTicket(null);
    }, []);

    // Cancel support subscription
    const cancelSupportSubscription = useCallback(async (reason) => {
        try {
            const response = await cancelSupportSubscriptionApi({ reason });
            if (response?.status === 200) {
                await getSupportSubscription();
            }
            return response;
        } catch (error) {
            console.error("Error canceling support subscription:", error);
            throw error;
        }
    }, [cancelSupportSubscriptionApi, getSupportSubscription]);

    // Pause support subscription
    const pauseSupportSubscription = useCallback(async () => {
        try {
            const response = await pauseSupportSubscriptionApi({});
            if (response?.status === 200) {
                await getSupportSubscription();
            }
            return response;
        } catch (error) {
            console.error("Error pausing support subscription:", error);
            throw error;
        }
    }, [pauseSupportSubscriptionApi, getSupportSubscription]);

    // Resume support subscription
    const resumeSupportSubscription = useCallback(async () => {
        try {
            const response = await resumeSupportSubscriptionApi({});
            if (response?.status === 200) {
                await getSupportSubscription();
            }
            return response;
        } catch (error) {
            console.error("Error resuming support subscription:", error);
            throw error;
        }
    }, [resumeSupportSubscriptionApi, getSupportSubscription]);

    // Refresh all data
    const refreshAll = useCallback(async () => {
        await Promise.all([
            getSupportAccess(),
            getSupportPlans(),
            getSupportSubscription(),
            getTickets(pagination.page, pagination.limit),
        ]);
    }, [getSupportAccess, getSupportPlans, getSupportSubscription, getTickets, pagination.page, pagination.limit]);

    // Initial data fetch
    useEffect(() => {
        if (user?.id) {
            getSupportAccess();
            getSupportPlans();
        }
    }, [user?.id, getSupportAccess, getSupportPlans]);

    return {
        // State
        supportAccess,
        supportPlans,
        supportSubscription,
        tickets,
        currentTicket,
        pagination,

        // Loading states
        isLoadingAccess,
        isLoadingPlans,
        isLoadingSubscription,
        isLoadingTickets,
        isLoadingTicket,
        isCreatingTicket,
        isAddingReply,
        isCreatingCheckout,
        isCancelingSubscription,
        isPausingSubscription,
        isResumingSubscription,

        // Errors
        accessError,
        plansError,
        subscriptionError,
        ticketsError,
        ticketError,
        createTicketError,
        addReplyError,
        checkoutError,
        cancelError,
        pauseError,
        resumeError,

        // Actions
        getSupportAccess,
        getSupportPlans,
        getSupportSubscription,
        getTickets,
        getTicket,
        createTicket,
        addReply,
        createSupportCheckout,
        clearCurrentTicket,
        refreshAll,
        cancelSupportSubscription,
        pauseSupportSubscription,
        resumeSupportSubscription,
    };
}

export function SupportProvider({ children }) {
    const support = useProvideSupport();
    return (
        <SupportContext.Provider value={support}>
            {children}
        </SupportContext.Provider>
    );
}

export function useSupport() {
    const context = useContext(SupportContext);
    if (!context) {
        throw new Error("useSupport must be used within a SupportProvider");
    }
    return context;
}

export default SupportContext;
